"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Info, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { displayCurrency } from "@/lib/utils";
import { clientAPI } from "@/lib/client-fetch";
import API_ENDPOINTS from "@/constants/api-endpoints";
import { useLocalDateChange } from "@/hooks/use-local-date-change";
import { useRefetchWhenStale } from "@/hooks/use-refetch-when-stale";
import { useStoreLocation } from "@/context/store-location-context";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

type TimeSlot = {
  bucket: string;
  explainer_text: string;
  is_available: boolean;
  is_sold_out: boolean;
  is_option_hidden: boolean;
};

type DeliveryDay = {
  isToday: boolean;
  dayString: string;
  fullDayString: string;
  dateString: string;
  value: string;
  isInitiallySelected: boolean;
  isSoldOut: boolean;
  timeSlots: TimeSlot[];
};

type ExpressDeliveryInfo = {
  is_express_delivery_option_hidden_from_user: boolean;
  is_express_delivery_crossed_out: boolean;
  express_delivery_fee: number;
  express_delivery_time_estimate_text: string;
};

type DeliveryWindowsResponse = {
  shipping_info_id_to_delivery_windows: Record<string, DeliveryDay[]>;
  shipping_info_id_to_express_delivery_info: Record<string, ExpressDeliveryInfo>;
  default_slots: DeliveryDay[];
};

const EXPRESS_SLOT_ID = "Express";

// Don't silently re-fetch on every tab refocus — only once data might realistically
// be stale (e.g. a slot sold out or expired while the tab sat unattended).
const STALE_AFTER_MS = 1000 * 60_000;

// The delivery_windows endpoint reads the city from the `City` header (as the
// native app sends it) and caches its response per city. Without the header,
// every web request shares one default cache entry, which can be days stale.
function getDeliveryWindows<T = DeliveryWindowsResponse>(city: string | null) {
  return clientAPI.get<T>(API_ENDPOINTS.DELIVERY_WINDOWS, { headers: { City: city ?? "" } });
}

function getDeliveryDaysForAddress(
  data: Pick<DeliveryWindowsResponse, "shipping_info_id_to_delivery_windows" | "default_slots">,
  addressId: string | number | null,
): DeliveryDay[] {
  if (!addressId) return data.default_slots;
  return data.shipping_info_id_to_delivery_windows[String(addressId)] || data.default_slots;
}

type DeliverySelectionCheck =
  | { valid: true; expressDeliveryFee?: number }
  | { valid: false; dayMissing: boolean };

// Single source of truth for "is this day/slot still bookable according to
// this payload?" — shared by the chooser's background revalidation and the
// checkout's pre-submit check, so both agree on what counts as stale.
function checkDeliverySelection(
  data: DeliveryWindowsResponse,
  addressId: string | number | null,
  dayValue: string,
  slotBucket: string,
): DeliverySelectionCheck {
  const day = getDeliveryDaysForAddress(data, addressId).find((d) => d.value === dayValue);
  if (!day) return { valid: false, dayMissing: true };

  if (slotBucket === EXPRESS_SLOT_ID) {
    const expressInfo = addressId
      ? data.shipping_info_id_to_express_delivery_info?.[String(addressId)]
      : null;
    const expressValid =
      day.isToday &&
      !!expressInfo &&
      !expressInfo.is_express_delivery_option_hidden_from_user &&
      !expressInfo.is_express_delivery_crossed_out;
    return expressValid
      ? { valid: true, expressDeliveryFee: expressInfo.express_delivery_fee }
      : { valid: false, dayMissing: false };
  }

  const slot = day.timeSlots.find((s) => s.bucket === slotBucket);
  const slotValid = !!slot && slot.is_available && !slot.is_sold_out && !slot.is_option_hidden;
  return slotValid ? { valid: true } : { valid: false, dayMissing: false };
}

// Sentinel distinct from any real selectedAddressId (string | number | null) so we can
// tell "first run" apart from "address explicitly cleared".
const ADDRESS_NOT_YET_TRACKED = Symbol("address-not-yet-tracked");

type DeliveryWindowChooserProps = {
  selectedAddressId: string | number | null;
  onSelectionChange?: (dayValue: string, slotBucket: string, dateString: string, expressDeliveryFee?: number) => void;
  // Called when a previously-made selection is no longer valid after a background
  // refetch (e.g. the slot sold out or the delivery day disappeared) — the parent
  // should clear its own copy of the selection and prompt the user to pick again.
  onSelectionInvalidated?: () => void;
  // Called when a background refetch finds the selected Express slot still
  // bookable but at a different fee, so the parent can update its totals.
  onExpressDeliveryFeeChange?: (expressDeliveryFee: number) => void;
  refetchTrigger?: number;
  readOnly?: boolean;
  deliveryFee?: number;
  // The parent's committed selection. The checkout accordion unmounts this
  // component while its section is collapsed, so on remount we restore (and
  // revalidate) this instead of resetting to a fresh preselection that would
  // disagree with what the parent is about to submit.
  selection?: { dayValue: string; slotBucket: string } | null;
};

export default function DeliveryWindowChooser({
  selectedAddressId,
  onSelectionChange,
  onSelectionInvalidated,
  onExpressDeliveryFeeChange,
  refetchTrigger,
  readOnly = false,
  deliveryFee,
  selection,
}: DeliveryWindowChooserProps) {
  const [deliveryDays, setDeliveryDays] = useState<DeliveryDay[]>([]);
  const [allDeliveryWindows, setAllDeliveryWindows] = useState<DeliveryWindowsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDayValue, setSelectedDayValue] = useState<string | null>(null);
  const [selectedSlotBucket, setSelectedSlotBucket] = useState<string | null>(null);
  const [expressDialogOpen, setExpressDialogOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const lastFetchedAtRef = useRef(0);
  const latestRequestIdRef = useRef(0);
  // Express fee last handed to the parent, to detect price changes on refetch.
  // undefined = unknown (e.g. selection restored from the parent), in which
  // case the parent's own pre-submit check catches any drift.
  const reportedExpressFeeRef = useRef<number | undefined>(undefined);
  const { storeLocation } = useStoreLocation();
  const city = storeLocation.city;
  const prevAddressIdRef = useRef<string | number | null | typeof ADDRESS_NOT_YET_TRACKED>(ADDRESS_NOT_YET_TRACKED);

  const selectedDay = deliveryDays.find((d) => d.value === selectedDayValue);

  // Express info for selected address
  const expressInfo: ExpressDeliveryInfo | null = (() => {
    if (!allDeliveryWindows || !selectedAddressId) return null;
    return allDeliveryWindows.shipping_info_id_to_express_delivery_info?.[String(selectedAddressId)] ?? null;
  })();

  const showExpressOption =
    selectedDay?.isToday &&
    !!expressInfo &&
    !expressInfo.is_express_delivery_option_hidden_from_user;

  // Fetch delivery windows on mount. `silent` skips the full-component loading
  // spinner — used for background refetches (tab refocus, post-submit re-check)
  // where we don't want to blow away the already-rendered selection UI.
  const fetchDeliveryWindows = useCallback(async (opts?: { silent?: boolean }) => {
    // Several triggers (mount, refetchTrigger, refocus, date change) can overlap;
    // only the most recent request may write state, so a slow older response
    // can't overwrite fresher data.
    const requestId = ++latestRequestIdRef.current;
    if (!opts?.silent) setIsLoading(true);
    const result = await getDeliveryWindows(city);
    if (requestId !== latestRequestIdRef.current) return;
    if (result.ok && result.data) {
      setAllDeliveryWindows(result.data);
      lastFetchedAtRef.current = Date.now();
    }
    // Unconditional: if this silent request superseded a non-silent one, the
    // superseded request bailed above and never cleared the spinner.
    setIsLoading(false);
  }, [city]);

  useEffect(() => {
    fetchDeliveryWindows();
  }, [fetchDeliveryWindows, refetchTrigger]);

  // If the user leaves this tab open in checkout and comes back later, the
  // delivery-windows payload fetched on mount (slot availability, sold-out
  // flags) can be stale. Re-fetch once past a staleness threshold so quick
  // alt-tabbing doesn't spam the endpoint.
  const refetchSilently = useCallback(() => fetchDeliveryWindows({ silent: true }), [fetchDeliveryWindows]);
  useRefetchWhenStale(refetchSilently, lastFetchedAtRef, STALE_AFTER_MS);

  // Day labels ("Hoy") and isToday come baked into the payload, so once the
  // user's calendar day rolls over they're wrong regardless of staleness
  // threshold. Re-fetch immediately on date change.
  useLocalDateChange(refetchSilently);

  // Update delivery days when address changes or data (re)loads.
  useEffect(() => {
    if (!allDeliveryWindows) return;

    const slots = getDeliveryDaysForAddress(allDeliveryWindows, selectedAddressId);
    setDeliveryDays(slots);
    const firstAvailable = slots.find((d) => d.isToday) || slots[0];

    const isFirstLoad = prevAddressIdRef.current === ADDRESS_NOT_YET_TRACKED;
    const addressChanged = prevAddressIdRef.current !== selectedAddressId;
    prevAddressIdRef.current = selectedAddressId;

    // On first load, adopt the parent's committed selection (we may be
    // remounting after the accordion section was collapsed) and fall through
    // to revalidate it against this fresh payload.
    let dayValue = selectedDayValue;
    let slotBucket = selectedSlotBucket;
    if (isFirstLoad && selection) {
      dayValue = selection.dayValue;
      slotBucket = selection.slotBucket;
      setSelectedDayValue(dayValue);
      setSelectedSlotBucket(slotBucket);
      reportedExpressFeeRef.current = undefined;
    } else if (addressChanged) {
      // New address (or first load with nothing committed): reset and
      // preselect the first available day.
      setSelectedDayValue(firstAvailable?.value ?? null);
      setSelectedSlotBucket(null);
      return;
    }

    // Same address, fresh data (e.g. refetch after tab refocus, a date
    // rollover or a failed submit): don't blow away the user's selection, but
    // revalidate it — if the day or slot they picked is no longer available,
    // clear it and let the parent know so it can prompt them to pick again.
    if (!dayValue) return;

    if (!slotBucket) {
      // Only a day preselection, which wasn't the user's commitment — if that
      // day is gone (typically yesterday's "Hoy"), quietly move to the first
      // available one.
      if (!slots.some((d) => d.value === dayValue)) {
        setSelectedDayValue(firstAvailable?.value ?? null);
      }
      return;
    }

    const check = checkDeliverySelection(allDeliveryWindows, selectedAddressId, dayValue, slotBucket);
    if (!check.valid) {
      if (check.dayMissing) setSelectedDayValue(firstAvailable?.value ?? null);
      setSelectedSlotBucket(null);
      onSelectionInvalidated?.();
      return;
    }

    // Express is still bookable but its price moved — keep the parent's
    // totals in sync with what will actually be charged.
    if (
      slotBucket === EXPRESS_SLOT_ID &&
      check.expressDeliveryFee !== undefined &&
      reportedExpressFeeRef.current !== undefined &&
      check.expressDeliveryFee !== reportedExpressFeeRef.current
    ) {
      reportedExpressFeeRef.current = check.expressDeliveryFee;
      onExpressDeliveryFeeChange?.(check.expressDeliveryFee);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDeliveryWindows, selectedAddressId]);

  // Update scroll button visibility
  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);

    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [deliveryDays]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.7;
    el.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  const handleDaySelect = (dayValue: string) => {
    setSelectedDayValue(dayValue);
    setSelectedSlotBucket(null); // Reset slot when day changes
  };

  const handleSlotSelect = (slotBucket: string) => {
    setSelectedSlotBucket(slotBucket);
    if (selectedDayValue && selectedDay) {
      const isExpress = slotBucket === EXPRESS_SLOT_ID;
      reportedExpressFeeRef.current = isExpress ? expressInfo?.express_delivery_fee : undefined;
      onSelectionChange?.(
        selectedDayValue,
        slotBucket,
        selectedDay.dateString,
        isExpress ? expressInfo?.express_delivery_fee : undefined,
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!selectedAddressId) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        Por favor selecciona un domicilio de entrega.
      </div>
    );
  }

  if (deliveryDays.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No hay ventanas de entrega disponibles.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Day pills - horizontal scroll with arrows */}
      <div className="relative">
        {/* Fade overlays */}
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-white to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-white to-transparent" />

        {/* Left arrow */}
        <button
          type="button"
          onClick={() => scroll("left")}
          className={cn(
            "absolute left-0 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow transition-opacity duration-200 hover:bg-gray-100 cursor-pointer",
            canScrollLeft ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        {/* Right arrow */}
        <button
          type="button"
          onClick={() => scroll("right")}
          className={cn(
            "absolute right-0 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow transition-opacity duration-200 hover:bg-gray-100 cursor-pointer",
            canScrollRight ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* Scrollable container */}
        <div
          ref={scrollRef}
          className="flex gap-2 overflow-x-auto pb-2 px-6 scrollbar-hide"
        >
          {deliveryDays.map((day) => {
            const hasAvailableSlots = day.timeSlots.some((s) => s.is_available && !s.is_sold_out);
            const isAvailable = !day.isSoldOut && hasAvailableSlots;
            const isSelected = day.value === selectedDayValue;

            return (
              <button
                key={day.value}
                onClick={() => handleDaySelect(day.value)}
                className={cn(
                  "flex-shrink-0 px-6 py-3 rounded-xl text-center transition min-w-[90px] cursor-pointer",
                  isSelected
                    ? "bg-teal-400 text-white"
                    : isAvailable
                    ? "bg-gray-100 hover:bg-gray-200 text-gray-900"
                    : "bg-gray-100 text-gray-400"
                )}
              >
                <div className="text-sm font-semibold">{day.dayString}</div>
                <div className={cn("text-sm", isSelected ? "text-white" : "text-muted-foreground")}>
                  {day.dateString}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Time slots */}
      {selectedDay && (
        readOnly ? (
          <ul className="space-y-1 text-sm px-4">
            {selectedDay.timeSlots
              .filter((slot) => !slot.is_option_hidden)
              .map((slot) => {
                const unavailable = slot.is_sold_out || !slot.is_available;
                return (
                  <li
                    key={slot.bucket}
                    className={cn(
                      "flex items-center gap-2",
                      unavailable ? "text-muted-foreground line-through" : "text-foreground"
                    )}
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-current flex-shrink-0" />
                    {slot.bucket}
                  </li>
                );
              })}
          </ul>
        ) : (
          <div className="space-y-1">
            {/* Express section — only for today when available */}
            {showExpressOption && (
              <>
                <div className="flex items-center gap-1.5 px-2 pt-1 pb-0.5">
                  <span className="text-sm font-semibold">Express</span>
                  <button
                    type="button"
                    className="text-teal-500 cursor-pointer"
                    onClick={() => setExpressDialogOpen(true)}
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </div>

                {/* Express slot row */}
                <div
                  onClick={() => !expressInfo!.is_express_delivery_crossed_out && handleSlotSelect(EXPRESS_SLOT_ID)}
                  className={cn(
                    "flex items-center gap-3 px-2 py-2 rounded-lg transition",
                    expressInfo!.is_express_delivery_crossed_out
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer hover:bg-gray-50"
                  )}
                >
                  <div
                    className={cn(
                      "h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                      selectedSlotBucket === EXPRESS_SLOT_ID ? "border-teal-500" : "border-gray-300"
                    )}
                  >
                    {selectedSlotBucket === EXPRESS_SLOT_ID && (
                      <div className="h-2.5 w-2.5 rounded-full bg-teal-500" />
                    )}
                  </div>
                  <span className={cn("flex-1 font-medium", expressInfo!.is_express_delivery_crossed_out && "line-through")}>
                    {expressInfo!.express_delivery_time_estimate_text}
                  </span>
                  <span className="text-teal-600 font-medium">
                    {displayCurrency(expressInfo!.express_delivery_fee)}
                  </span>
                </div>

                {/* Programado section header */}
                <div className="px-2 pt-3 pb-0.5">
                  <p className="text-sm font-semibold">Programado</p>
                  <p className="text-xs text-muted-foreground">Pide 10 minutos antes del horario de entrega</p>
                </div>
              </>
            )}

            {/* Hint text when no express section */}
            {!showExpressOption && (
              <p className="text-sm text-foreground font-medium px-2">
                Pide 10 minutos antes del horario de entrega
              </p>
            )}

            {/* Scheduled slots */}
            {selectedDay.timeSlots
              .filter((slot) => !slot.is_option_hidden)
              .map((slot) => {
                const isSelectable = slot.is_available && !slot.is_sold_out;
                const isSelected = slot.bucket === selectedSlotBucket;

                return (
                  <div
                    key={slot.bucket}
                    onClick={() => isSelectable && handleSlotSelect(slot.bucket)}
                    className={cn(
                      "flex items-center gap-3 px-2 py-2 rounded-lg transition",
                      isSelectable ? "cursor-pointer hover:bg-gray-50" : "cursor-not-allowed opacity-50",
                    )}
                  >
                    {/* Radio indicator */}
                    <div
                      className={cn(
                        "h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                        isSelected ? "border-teal-500" : "border-gray-300"
                      )}
                    >
                      {isSelected && (
                        <div className="h-2.5 w-2.5 rounded-full bg-teal-500" />
                      )}
                    </div>

                    {/* Slot time */}
                    <span className={cn("flex-1 font-medium", slot.is_sold_out && "line-through")}>
                      {slot.bucket}
                    </span>

                    {/* Sold out or Price */}
                    {slot.is_sold_out ? (
                      <span className="text-sm text-muted-foreground">Agotado</span>
                    ) : !slot.is_available ? (
                      <span className="text-sm text-muted-foreground">No disponible</span>
                    ) : deliveryFee !== undefined ? (
                      <span className="text-teal-600 font-medium">
                        {displayCurrency(deliveryFee)}
                      </span>
                    ) : null}
                  </div>
                );
              })}
          </div>
        )
      )}

      {/* Express info dialog */}
      <Dialog open={expressDialogOpen} onOpenChange={setExpressDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Entregas Express</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Para brindarte aún más conveniencia, ahora puedes solicitar entregas express (en menos de 1 hora). Se entregarán a través de socios repartidores terceros. El tiempo de entrega y costo pueden variar dependiendo de disponibilidad y ubicación.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpressDialogOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export function getSelectedDeliveryWindow(dayValue: string, slotBucket: string): { date: string; time: string } | null {
  return {
    date: dayValue,
    time: slotBucket,
  };
}

export type { DeliveryDay, TimeSlot, ExpressDeliveryInfo, DeliveryWindowsResponse };
export { EXPRESS_SLOT_ID, STALE_AFTER_MS, getDeliveryWindows, getDeliveryDaysForAddress, checkDeliverySelection };
